import $ from 'jquery';
import PropTypes from 'prop-types';
import React from 'react';

import InstitutionConstants, {Institution} from 'institutions/InstitutionConstants';
import LegacySelect2, {LegacySelect2ChangeEvent} from 'ui-library/LegacySelect2';

export interface InstitutionSelectorProps {
  className?: string;
  name?: string;
  value?: Institution;
  onChange: (institution: Institution) => void;
  placeholder?: string;
  large?: boolean;
  allowUnverified?: boolean;
  allowUnverifiedSearch?: boolean;
  disabled?: boolean;
  allowLeaving?: boolean;
}

interface InstitutionOption {
  id: string;
  disabled?: boolean;
  text: string;
  institution?: Institution;
}

const TOO_MANY_RESULTS_OPTION: InstitutionOption = {
  id: '-1',
  disabled: true,
  text: 'Too many institutions found\u2014please refine your search.',
};

function institutionId(institution: Institution | undefined): string {
  return (institution && institution.id) || '';
}

function institutionOption(institution: Institution): InstitutionOption {
  const suffix = institution.licensed ? 'licensed' : institution.verified ? '' : 'unverified';
  const text = suffix ? `${institution.name} (${suffix})` : institution.name;
  return {
    text,
    id: institutionId(institution),
    institution,
  };
}

export function createUserSuppliedInstitutionOption(term: string): InstitutionOption {
  return institutionOption({
    id: `${InstitutionConstants.USER_SUPPLIED_ID_PREFIX}${term}`,
    name: term,
    fullName: term,
    shortName: '',
    names: [],
    subdomains: [],
    urlIdentifier: '',
    verified: false,
    licensed: false,
    hasUnlimitedSpace: false,
    grantsElnDesktopSync: false,
    adminEmail: '',
    enableExportNotifications: false,
    enableEmailChangeNotifications: false,
  });
}

// LegacySelect2 for institutions that exposes an input[name=institution]
export default class InstitutionSelector extends React.Component<InstitutionSelectorProps> {
  static propTypes = {
    className: PropTypes.string,
    name: PropTypes.string.isRequired,
    value: InstitutionConstants.PropTypes.institution,
    onChange: PropTypes.func,
    placeholder: PropTypes.string.isRequired,
    large: PropTypes.bool.isRequired,
    allowUnverified: PropTypes.bool.isRequired, // allows creating new unverified institutions
    allowUnverifiedSearch: PropTypes.bool.isRequired, // allows searching for unverified institutions
    disabled: PropTypes.bool,
    allowLeaving: PropTypes.bool,
  };

  static defaultProps: InstitutionSelectorProps = {
    name: 'institution',
    placeholder: 'Institution',
    onChange: (institution: Institution) => {},
    large: false,
    allowUnverified: true,
    allowUnverifiedSearch: false,
  };

  institutionChangeHandler = (e: LegacySelect2ChangeEvent & {choice: {institution: Institution}}): void => {
    this.props.onChange(e.choice.institution);
  };

  createSearchChoice = (term: string, options: Array<InstitutionOption>): InstitutionOption | null => {
    if (!this.props.allowUnverified) {
      return null;
    }
    // Only create a manual institution if the term is non-empty and doesn't match an existing one.
    term = term.trim();
    const nameMatches = (name: string): boolean => name.toUpperCase() === term.toUpperCase();
    if (
      !term ||
      options.some((option) =>
        Boolean(
          option !== TOO_MANY_RESULTS_OPTION &&
            option.institution &&
            option.institution.names.some((name) => nameMatches(name))
        )
      )
    ) {
      return null;
    }
    return createUserSuppliedInstitutionOption(term);
  };

  render(): JSX.Element {
    return (
      <LegacySelect2
        name={this.props.name}
        large={this.props.large}
        placeholder={this.props.placeholder}
        className={this.props.className}
        allowClear={this.props.allowLeaving}
        disabled={false}
        disableInput={this.props.disabled}
        value={institutionId(this.props.value)}
        onChange={this.institutionChangeHandler}
        minimumInputLength={1}
        formatInputTooShort={() => 'Please enter 1 or more characters to search for your institution.'}
        initSelection={(elt, callback) => {
          if (this.props.value) {
            callback(institutionOption(this.props.value));
          }
        }}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formatResult={function formatResult(result: InstitutionOption, ...rest: Array<any>): string {
          if (result === TOO_MANY_RESULTS_OPTION) {
            return TOO_MANY_RESULTS_OPTION.text;
          }
          const defaultFormatResult = $.fn.select2.defaults.formatResult;
          // @ts-expect-error
          return defaultFormatResult.call(this, result, ...rest);
        }}
        createSearchChoice={this.createSearchChoice}
        createSearchChoicePosition="bottom"
        ajax={{
          url: '/1/api/institutions/',
          dataType: 'json',
          data: (term: string) => ({q: term, allowUnverified: this.props.allowUnverifiedSearch}),
          results: ({results, more}: {results: Array<Institution>; more: boolean}) => {
            const formattedResults = results.map((institution) => institutionOption(institution));
            if (more) {
              formattedResults.push(TOO_MANY_RESULTS_OPTION);
            }
            return {results: formattedResults};
          },
        }}
      />
    );
  }
}
